import type { Language } from "@/lib/i18n";
import {
  BROKER_GUIDES_PT_PT,
  IB_API_GUIDE_PT_PT,
  T212_API_GUIDE_PT_PT,
} from "./brokerGuides.pt-PT";

export interface GuideStep {
  title: string;
  description: string;
  image?: string;
}

export interface BrokerGuide {
  brokerName: string;
  intro: string;
  steps: GuideStep[];
  note?: string;
  /**
   * Render screenshots at full container width instead of the narrow default.
   * Set for guides captured from a desktop UI (wide landscape screenshots), where the
   * narrow default shrinks callout numbers past legibility. Phone-captured guides look
   * better constrained.
   */
  wideImages?: boolean;
}

export const BROKER_GUIDES: Record<string, BrokerGuide> = {
  t212: {
    brokerName: "Trading 212",
    intro:
      "Export your full transaction history from the T212 mobile app in three taps.",
    steps: [
      {
        title: "Open History",
        description:
          "Open the Trading 212 app and tap the icon in the bottom-right corner, then select History.",
        image: "/guides/t212/Get_CSVs/step-1.jpg",
      },
      {
        title: "Tap the export icon",
        description:
          "In the top-right corner of the History screen, tap the export icon.",
        image: "/guides/t212/Get_CSVs/step-2.jpg",
      },
      {
        title: "Select your date range and export",
        description:
          "T212 exports up to 1 year per file. Select all the data you want exported. Tap Export, the CSV will be downloaded to your device. Repeat for each year if needed.",
        image: "/guides/t212/Get_CSVs/step-3.jpg",
      },
    ],
    note: "T212 limits exports to 1 year per file. If your history spans multiple years, export one file per year and select them all at once in Oitava's import dialog.",
  },
  ibkr: {
    brokerName: "Interactive Brokers",
    intro: "Download your activity statement from the IBKR Client Portal.",
    steps: [
      {
        title: "Log in to Client Portal",
        description: "Go to clientportal.ibkr.com and log in to your account.",
      },
      {
        title: "Open Reports",
        description: "Navigate to Performance & Reports → Statements.",
      },
      {
        title: "Generate an Activity Statement",
        description:
          'Select "Activity" as the statement type, set your date range to cover your full history, and choose CSV as the format.',
      },
      {
        title: "Download and import",
        description:
          "Click Run, then download the file. Upload it in Oitava's import dialog.",
      },
    ],
  },
  degiro: {
    brokerName: "DEGIRO",
    intro:
      "Export your activity from the DEGIRO web platform. Make sure to use the Account Statement, not the Transactions export.",
    steps: [
      {
        title: "Go to Inbox > Account Statement",
        description:
          "Open the Inbox (bottom-right) and select Account Statement. This is the correct file, not the Transactions tab, which only contains buy/sell data and misses dividends, fees, and interest.",
        image: "/guides/degiro/step_1.jpg",
      },
      {
        title: "Select your date range and export as CSV",
        description:
          "Pick the date range covering the transactions you want to import. Click Export. If you're unsure, select your full history. Oitava detects and ignores duplicates automatically, so overlapping imports are safe.",
        image: "/guides/degiro/step_2.jpg",
      },
      {
        title: "Upload in Oitava",
        description:
          "Choose CSV, download the file, then use the Import button on the Transactions page to upload it.",
        image: "/guides/degiro/step_3.jpg",
      },
    ],
  },
  robinhood: {
    brokerName: "Robinhood",
    intro:
      "Generate an account activity report in the Robinhood app and download it as a CSV.",
    steps: [
      {
        title: "Open your account menu",
        description:
          "In the Robinhood app, tap the account icon in the bottom-right corner (1), then tap Reports and statements (2).",
        image: "/guides/Robinhood/step_1.jpg",
      },
      {
        title: "Open Account activity reports",
        description:
          "Tap Account activity reports, then Reports (3). This is the CSV export. The Monthly statements below it are PDFs, which Oitava cannot read.",
        image: "/guides/Robinhood/step_2.jpg",
      },
      {
        title: "Start a new report",
        description:
          "Any reports you have generated before are listed here. Tap Generate new report (4).",
        image: "/guides/Robinhood/step_3.jpg",
      },
      {
        title: "Set the date range",
        description:
          "Enter a start and end date covering your full history (5), then tap Generate report (6). Set the start date to before your first ever deposit, otherwise the earlier transactions will be missing.",
        image: "/guides/Robinhood/step_4.jpg",
      },
      {
        title: "Download and import",
        description:
          "The report takes a moment to prepare and Robinhood notifies you when it is ready. Tap the download icon next to it, then upload the CSV in Oitava's import dialog.",
        image: "/guides/Robinhood/step_5.jpg",
      },
    ],
    note: "Robinhood Europe support is in beta. The account activity report covers deposits, crypto purchases and sales, transfers, staking earnings and crypto rewards. It does not include yield payments on your cash balance: Robinhood only puts those in the monthly PDF statements, one file per month, so add them yourself as Interest transactions in Oitava.",
  },
  xtb: {
    brokerName: "XTB",
    intro:
      "Export your transaction history from the XTB app, covering every currency account you hold.",
    steps: [
      {
        title: "Open your account menu",
        description:
          "In the XTB app, tap the profile icon in the top-left corner.",
        image: "/guides/XTB/step_1.png",
      },
      {
        title: "Open All operations history",
        description: "Scroll down and tap All operations history.",
        image: "/guides/XTB/step_2.png",
      },
      {
        title: "Switch to Cash operations and export",
        description:
          "Tap the Cash operations tab, then tap the download icon in the top-right corner.",
        image: "/guides/XTB/step_3.png",
      },
      {
        title: "Start a new report",
        description: "Tap New report.",
        image: "/guides/XTB/step_4.png",
      },
      {
        title: "Select the full date range and every account",
        description:
          "Set the date range to All to cover your full history. Under Accounts, tick every account listed, not just the one shown by default. XTB gives each currency (EUR, USD, etc.) its own separate account, and if you only tick one, transactions in the others won't be in the export. Then tap Generate report.",
        image: "/guides/XTB/step_5.png",
      },
      {
        title: "Download and import",
        description:
          "Tap Download report. If you ticked more than one account, XTB downloads a separate file for each, for example one for your EUR account and one for your USD account. Import them all into one XTB portfolio in Oitava, or each into its own portfolio. The note below explains both options.",
        image: "/guides/XTB/step_6.png",
      },
    ],
    note: "XTB gives each currency (EUR, USD, etc.) its own account, and lets you open extra accounts too, for example one per family member. Each account you tick comes as its own file. New deposits always land in your first account, and money you move to the others shows up in XTB as a transfer. Oitava imports each transfer as a withdrawal from the account it left and a deposit into the account it reached, including transfers that convert currency.\n\nOne portfolio for all your XTB accounts: import every file into the same XTB portfolio. You get a single view of all your XTB holdings, with cash kept per currency. Transfers between your accounts cancel out, so your cash and invested amount stay right, but they still appear in this portfolio's deposit and withdrawal history.\n\nOne portfolio per XTB account: create a portfolio for each account and import each file into its own portfolio. Each portfolio shows only that account's holdings and cash. Your first account will show a withdrawal every time you move money to another account, and the receiving portfolio shows it as a deposit. Your returns are not affected, and the All Portfolios view adds them back together.\n\nEither way, tick every account in the Accounts list before generating the report. If you leave one out, money moved into it will look like it left XTB.",
  },
};

export const T212_API_GUIDE: BrokerGuide = {
  brokerName: "Trading 212",
  intro:
    "Generate a Personal Access Token from the Trading 212 app in four steps.",
  steps: [
    {
      title: "Open your account menu",
      description:
        "In the Trading 212 app, tap the icon in the bottom-right corner, then tap Settings.",
      image: "/guides/t212/Get_API_KEY/step1.jpg",
    },
    {
      title: "Navigate to the API section",
      description:
        "Scroll down and tap API (Personal Access). You may need to enable it first if you haven't already.",
      image: "/guides/t212/Get_API_KEY/step2.jpg",
    },
    {
      title: "Tap Generate API key",
      description:
        "On the API (Beta) screen, tap Generate API key to open the New API key screen.",
      image: "/guides/t212/Get_API_KEY/step3.jpg",
    },
    {
      title: "Select the required permissions",
      description:
        "Give the key a name, then turn on History, History - Dividends, History - Orders, History - Transactions, and Metadata. Leave the rest off. Oitava only reads your history, it never places orders or touches your Pies. Tap Generate key.",
      image: "/guides/t212/Get_API_KEY/step4.jpeg",
    },
  ],
  note: "Copy both the API key ID and the secret key shown after generating, you'll need both, and the secret is only shown once. Use the Live key for real-money portfolios and the Demo key for paper trading.",
};

export const IB_API_GUIDE: BrokerGuide = {
  brokerName: "Interactive Brokers",
  wideImages: true,
  intro:
    "IBKR sync needs two things from the Client Portal: an Activity Flex Query that says which data to send, and a Flex Web Service token that lets Oitava request it. You set this up once, after that syncing is automatic and you never need to come back here.",
  steps: [
    {
      title: "Open the Flex Web Service settings",
      description:
        "In Client Portal, go to Performance & Reports → Flex Queries. On the right, in the Flex Web Service Configuration panel: (1) click the gear icon.",
      image: "/guides/ibkr/1_flexqueries.png",
    },
    {
      title: "Enable the service and generate your token",
      description:
        '(1) Tick Flex Web Service Status. (2) Click Generate New Token, an "Expire after" dropdown appears, so choose 1 year, because the default of 6 hours would stop your sync working the same day. (3) Click Save. (4) Copy the Current Token.\n\nLeave "Valid for IP Address" empty.',
      image: "/guides/ibkr/2_configure_flex_web_service.png",
    },
    {
      title: "Start the AI query builder",
      description:
        "Go back to Flex Queries. In the Configure with AI panel at the top: (1) click Configure with AI.",
      image: "/guides/ibkr/3_connfigure_with_AI.png",
    },
    {
      title: "Paste this prompt",
      description:
        '(1) Paste the following into the description box, then (2) click Generate Flex Query:\n\n"Activity Flex Query, XML, period Last 365 Calendar Days. Sections: Trades, Cash Transactions, Corporate Actions, Financial Instrument Information, Include Currency Rates. Select all fields in each"\n\nIBKR limits this box to 200 characters, so the prompt is written to fit.',
      image: "/guides/ibkr/3_generate_query_AI.png",
    },
    {
      title: "Switch on Include Currency Rates",
      description:
        'The AI ignores this one, so it needs setting by hand. On the Review Flex Query screen: (1) click Edit Manually, (2) find "Include Currency Rates?" under General Configuration and set it to Yes, (3) click Save.\n\nThis lets Oitava use IBKR\'s own exchange rates for your transactions instead of estimating them.',
      image: "/guides/ibkr/4_include_currency_rates.png",
    },
    {
      title: "Copy the Query ID",
      description:
        "At the top of Review Flex Query: (1) copy the Query ID, a 7-digit number. Don't confuse it with your account number, which starts with a U.",
      image: "/guides/ibkr/5_copy_query_id.png",
    },
    {
      title: "Paste both into Oitava",
      description:
        "Enter the token and the Query ID in your portfolio's sync settings. Both are encrypted before storage and the token is read-only. It can retrieve statements but cannot trade or move money.",
    },
  ],
  note: "Prefer to build the query yourself instead of using the AI? Click the + next to Activity Flex Query and tick five sections, Trades, Cash Transactions, Corporate Actions, Financial Instrument Information, and Include Currency Rates, hitting Select All inside each. You don't need to pick individual fields. Cash Transactions is the one people miss: it carries your dividends, deposits, withdrawals, fees and interest, and without it Oitava sees your trades but none of your cash. Corporate Actions is what lets Oitava credit the cash from a dividend right, which IBKR books under the right's own name, to the company that paid it.\n\nA few limits worth knowing. IBKR only makes the last four calendar years plus the current year available through Flex, so if your account is older than that, sync what it can reach and import a CSV activity statement for the earlier years. Each request is capped at 365 days, so longer histories are fetched in batches. Flex data covers up to the previous business day, meaning today's trades appear tomorrow, and brand-new accounts awaiting their first funding can't produce statements at all yet, so give it a day after opening. Generating a new token invalidates the previous one, so if you re-generate it later, update it in Oitava too.",
};

export function detectBrokerageKey(
  brokerage: string | null | undefined,
): string {
  const b = (brokerage ?? "").toLowerCase();
  if (
    b.includes("t212") ||
    b.includes("trading 212") ||
    b.includes("trading212")
  )
    return "t212";
  if (
    // Portfolios store the brokerage as the bare code "ib", which none of the longer
    // spellings below match. Exact-compared rather than via includes() so it can't
    // accidentally catch an unrelated brokerage that happens to contain "ib".
    b === "ib" ||
    b.includes("ibkr") ||
    b.includes("interactive brokers") ||
    b.includes("interactivebrokers")
  )
    return "ibkr";
  if (b.includes("degiro") || b.includes("de giro")) return "degiro";
  if (b.includes("xtb")) return "xtb";
  if (b.includes("robinhood")) return "robinhood";
  return "unknown";
}

/** The broker guides in the given language; untranslated guides stay in English. */
export function getBrokerGuides(language: Language) {
  if (language === "en") {
    return {
      brokerGuides: BROKER_GUIDES,
      t212ApiGuide: T212_API_GUIDE,
      ibApiGuide: IB_API_GUIDE,
    };
  }
  return {
    brokerGuides: { ...BROKER_GUIDES, ...BROKER_GUIDES_PT_PT },
    t212ApiGuide: T212_API_GUIDE_PT_PT ?? T212_API_GUIDE,
    ibApiGuide: IB_API_GUIDE_PT_PT ?? IB_API_GUIDE,
  };
}
